import React from 'react';
import { Redirect } from 'react-router-dom';
import { Theme, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { LoginForm } from 'features/auth/components/LoginForm';
import { CookieService } from 'features/auth/services/CookieService';
import routes from 'constants/routes';
import bc from '../../assets/background.webp';
import logo from '../../assets/logo.svg';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		page: {
			width: '100%',
			minHeight: '100vh',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			background: `linear-gradient(135deg, rgba(37,19,57,0.9) 0%, rgba(74,39,108,0.85) 50%, rgba(37,19,57,0.9) 100%), url(${bc})`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			padding: theme.spacing(3),
			boxSizing: 'border-box',
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(1.5, 1),
			},
		},
		mainContainer: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			maxWidth: '500px',
			width: '100%',
			gap: theme.spacing(2.5),
			boxSizing: 'border-box',
			[theme.breakpoints.down('sm')]: {
				gap: theme.spacing(1.5),
				maxWidth: '100%',
				width: '100%',
			},
		},
		logoSection: {
			textAlign: 'center',
			marginBottom: theme.spacing(1),
		},
		logo: {
			height: '4rem',
			filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
			[theme.breakpoints.down('sm')]: {
				height: '3rem',
			},
		},
		welcomeCard: {
			background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
			backdropFilter: 'blur(20px)',
			borderRadius: '20px',
			padding: theme.spacing(4),
			textAlign: 'center',
			boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.2)',
			width: '100%',
			border: '1px solid rgba(255,255,255,0.3)',
			boxSizing: 'border-box',
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(2),
				borderRadius: '16px',
			},
		},
		title: {
			background: 'linear-gradient(135deg, #1976d2 0%, #673ab7 100%)',
			backgroundClip: 'text',
			WebkitBackgroundClip: 'text',
			WebkitTextFillColor: 'transparent',
			fontWeight: 700,
			marginBottom: theme.spacing(2),
			fontSize: '1.8rem',
			[theme.breakpoints.down('sm')]: {
				fontSize: '1.5rem',
			},
		},
		subtitle: {
			color: theme.palette.text.primary,
			lineHeight: 1.7,
			marginBottom: theme.spacing(2),
			fontSize: '1rem',
			'& strong': {
				color: theme.palette.primary.main,
				fontWeight: 600,
			},
		},
		redirectCard: {
			background: 'rgba(255,255,255,0.1)',
			backdropFilter: 'blur(10px)',
			borderRadius: '12px',
			padding: theme.spacing(2),
			textAlign: 'center',
			border: '1px solid rgba(255,255,255,0.2)',
			width: '100%',
			boxSizing: 'border-box',
			[theme.breakpoints.down('sm')]: {
				padding: theme.spacing(1.5),
			},
		},
		redirectText: {
			color: 'rgba(255,255,255,0.9)',
			fontSize: '0.875rem',
			lineHeight: 1.5,
		},
		linkText: {
			color: '#fff',
			textDecoration: 'none',
			fontWeight: 600,
			borderBottom: '1px solid rgba(255,255,255,0.5)',
			transition: 'all 0.3s ease',
			'&:hover': {
				borderBottomColor: '#fff',
				color: '#fff',
			},
		},
	}),
);

const Login = (): JSX.Element => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const token = CookieService.getToken();

	if (token) {
		return <Redirect to={routes.home} />;
	}

	return (
		<div className={classes.page}>
			<div className={classes.mainContainer}>
				{/* Logo Section */}
				<div className={classes.logoSection}>
					<img className={classes.logo} src={logo} alt="mevuelo.com logo" />
				</div>

				{/* Welcome Message Card */}
				<Paper className={classes.welcomeCard} elevation={0}>
					<Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className={classes.title}>
						¡Hola, equipo!
					</Typography>

					<Typography variant="body1" className={classes.subtitle}>
						<strong>Bienvenidos a Power</strong>, nuestro centro de gestión y acción 🚀
					</Typography>

					<Typography variant="body1" className={classes.subtitle}>
						Este espacio es exclusivo para el staff de <strong>mevuelo.com</strong> y nuestras subsidiarias en cada uno
						de los países donde nos volamos.
					</Typography>

					<Typography variant="body1" className={classes.subtitle} style={{ marginBottom: 0 }}>
						<strong>Gracias por formar parte de este viaje.</strong>
						<br />
						Cada ingreso cuenta, cada aporte suma, y juntos seguimos transformando la manera de viajar.
					</Typography>
				</Paper>

				{/* Login Form */}
				<LoginForm />

				{/* Redirect Message */}
				<Paper className={classes.redirectCard} elevation={0}>
					<Typography className={classes.redirectText}>
						👉 Si llegaste aquí por error, hacé clic en el{' '}
						<a className={classes.linkText} href="https://mevuelo.com" target="_blank" rel="noopener noreferrer">
							siguiente enlace
						</a>{' '}
						para ir al sitio de clientes o contactá a tu asesor.
					</Typography>
				</Paper>
			</div>
		</div>
	);
};

export default Login;
